/**
 * LE MODE EXPLORER D'UN TRACÉ — des curseurs sur une courbe (atelier de
 * figures, lot L2, F1 ; `docs/pilotage/atelier-figures/CONCEPTION.md` § 2 ;
 * SPEC-REPRESENTATION-VISUELLE § 6.5, § 6.9).
 *
 * ─── Ce que ce fichier fait, et ne fait pas ─────────────────────────────────
 *
 * L'élève règle a, h, k (ou b, ou le Z d'une droite baladeuse) et regarde la
 * courbe bouger. Ce qu'il soumet, en mode Répondre, est une CONSTRUCTION
 * ordinaire : une `fonction` de la famille, aux paramètres réglés. Le serveur
 * la juge comme tout tracé (`jugerTrace`, contrainte `courbe_egale`) : aucune
 * voie de correction neuve, aucun modèle (décision 5 du fondateur : sur une
 * figure, le code juge).
 *
 * Ici, du code PUR, partagé par l'application (les curseurs), l'import (la
 * forme du réglage) et la correction (l'écart nommé sur UN paramètre) :
 *
 *  - `valeursDeDepart`, `parametresDuReglage`, `documentDuReglage` : des
 *    valeurs des curseurs au document que l'élève soumet ;
 *  - `figureDuReglage` : la courbe courante, et l'ÉCHO pâle des deux positions
 *    précédentes (état de l'écran seulement, jamais enregistré) ;
 *  - `griefsDuReglage` : ce que l'import refuse ;
 *  - `ecartDUnParametre` : après la correction seulement, l'écart dit sur UN
 *    paramètre (« Ton sommet est en (2 ; −3) ; celui de la règle, en (−2 ; −3). »).
 *
 * Rien ici ne connaît la réponse AVANT la soumission : `documentDuReglage` et
 * `figureDuReglage` ne reçoivent que le réglage de l'énoncé et les valeurs
 * des curseurs. L'écran ne peut donc montrer aucun retour de justesse
 * (§ 6.9.1) — c'est une propriété de leur signature.
 */
import { documentDeTrace, } from '../types/manuel-exercices.js';
import { resoudreConstruction, versFigureV2, } from './construction.js';
import { decrireConstruction } from './decrire-v2.js';
import { dessinerPlan, evaluerFonctionV2, fonctionCanoniqueV2, nombreOuPiV2, texteEnPiV2, } from './figure-v2.js';
import { formaterNombre } from './geometrie.js';
/** Un nombre à lire : virgule décimale, signe moins typographique. */
export function nombreSigne(v) {
    return formaterNombre(v).replace('-', '−');
}
/** L'identifiant de la courbe que l'élève règle, dans son document (motif de l'atelier). */
export const ID_COURBE_REGLEE = 'courbe';
/** Le préfixe des objets d'ÉCHO : l'écran les peint pâles, sous la courbe vive. */
export const PREFIXE_ECHO = 'echo:';
/** Deux positions précédentes, pas trois (F24, H3 de la spécification). */
export const ECHOS_MAX = 2;
/** Au-delà, un curseur a trop de crans pour se régler au doigt. */
export const CRANS_MAX_CURSEUR = 400;
/** Le nom du curseur d'une droite baladeuse. */
export const CURSEUR_BALADEUSE = 'Z';
/* ─────────────────────────────── les valeurs ─────────────────────────────── */
/** Les curseurs à leur position de départ — celle où la bascule vers Répondre les ramène. */
export function valeursDeDepart(reglage) {
    return Object.fromEntries(reglage.curseurs.map((c) => [c.nom, c.depart]));
}
/**
 * Une valeur bornée et AIMANTÉE au pas du curseur. Les bornes sont des
 * multiples du pas (l'import le tient) : l'aimantation depuis zéro les respecte.
 */
export function aimanterCurseur(valeur, c) {
    const borne = Math.min(Math.max(valeur, c.min), c.max);
    if (!Number.isFinite(borne))
        return c.depart;
    return Number((Math.round(borne / c.pas) * c.pas).toFixed(6));
}
/** Les paramètres de la fonction tracée, des valeurs des curseurs. */
export function parametresDuReglage(reglage, valeurs) {
    if (reglage.baladeuse !== undefined) {
        /* x·X + y·Y = Z  ⇔  Y = (−x/y)·X + Z/y. */
        const { x, y } = reglage.baladeuse;
        const z = valeurs[CURSEUR_BALADEUSE] ?? reglage.curseurs[0]?.depart ?? 0;
        return { a: propre(-x / y), b: propre(z / y) };
    }
    const sortie = { ...(reglage.fixes ?? {}) };
    for (const c of reglage.curseurs)
        sortie[c.nom] = valeurs[c.nom] ?? c.depart;
    return sortie;
}
/**
 * Le DOCUMENT que l'élève soumet en mode Répondre : la courbe réglée, posée
 * sur le repère de l'exercice. Le même que l'atelier rapporterait d'un tracé
 * à l'outil Fonction — la correction n'en voit pas la différence.
 */
export function documentDuReglage(repere, options, reglage, valeurs) {
    const objet = {
        id: ID_COURBE_REGLEE,
        /* Le nom lu par la relecture du geste et le lecteur d'écran : « f(x) = 2x² ». */
        nom: 'f',
        def: {
            type: 'fonction',
            famille: reglage.famille,
            parametres: parametresDuReglage(reglage, valeurs),
            ...(reglage.domaine !== undefined ? { domaine: reglage.domaine } : {}),
        },
    };
    return documentDeTrace(repere, { calque: options.calque, objets: [objet] });
}
/** « a = 2, h = −1, k = 3 » : l'état des curseurs, pour qui ne les voit pas. */
export function decrireValeursReglage(reglage, valeurs) {
    return reglage.curseurs
        .map((c) => `${c.nom} = ${nombreSigne(valeurs[c.nom] ?? c.depart)}`)
        .join(', ');
}
/* ─────────────────────────────── le dessin ─────────────────────────────── */
/**
 * La courbe courante, et l'ÉCHO des positions précédentes, en pointillé, sous
 * elle. Les objets d'écho portent un identifiant `echo:…` : l'écran les peint
 * pâles. Le calque de l'énoncé (la courbe « qu'on cherche » en pointillé, s'il
 * y en a une) reste dessous tout.
 */
export function figureDuReglage(courant, echos, enonce) {
    const resolue = resoudreConstruction(courant, enonce);
    const figure = versFigureV2(resolue, { calque: true });
    const pales = echos.slice(0, ECHOS_MAX).flatMap((doc, i) => versFigureV2(resoudreConstruction(doc, enonce), { calque: false })
        .objets.filter((o) => o.type === 'fonction')
        .map((o) => ({ ...o, id: `${PREFIXE_ECHO}${i}`, ton: 'enonce', pointille: true })));
    return {
        liste: dessinerPlan({
            ...figure,
            objets: [
                ...figure.objets.filter((o) => o.ton !== 'eleve'),
                ...pales,
                ...figure.objets.filter((o) => o.ton === 'eleve'),
            ],
        }, { cadre: 'repere' }),
        description: decrireConstruction(courant, resolue).description,
    };
}
/* ─────────────────────────────── l'import ─────────────────────────────── */
const EPS = 1e-6;
const multipleDe = (v, pas) => Math.abs(v / pas - Math.round(v / pas)) < EPS;
/** Une valeur attendue écrite par la contrainte : un nombre, ou un nombre en π. */
const valeurEcrite = (v) => nombreOuPiV2(v);
/**
 * Ce que l'import refuse d'un réglage. Il le lit CONTRE le corrigé : la cible
 * réglée existe et c'est une courbe ; une contrainte `courbe_egale` la vise,
 * de la même famille et écrite avec les mêmes lettres ; chaque valeur attendue
 * est atteignable au pas du curseur ; et le départ n'est PAS la réponse.
 */
export function griefsDuReglage(reglage, corrige) {
    const griefs = [];
    const dire = (g) => {
        griefs.push(g);
    };
    const cible = corrige.cibles.find((c) => c.id === reglage.objet);
    if (cible === undefined)
        dire(`la cible « ${reglage.objet} » n’est pas dans \`cibles\``);
    else if (cible.genre !== 'fonction')
        dire('la cible réglée est une courbe (genre `fonction`)');
    /* Chaque curseur, pour lui-même. */
    const noms = new Set();
    for (const c of reglage.curseurs) {
        if (noms.has(c.nom))
            dire(`le curseur « ${c.nom} » est déclaré deux fois`);
        noms.add(c.nom);
        if (!(c.max > c.min))
            dire(`curseur ${c.nom} : min doit être inférieur à max`);
        else if ((c.max - c.min) / c.pas > CRANS_MAX_CURSEUR) {
            dire(`curseur ${c.nom} : plus de ${CRANS_MAX_CURSEUR} crans ; agrandis le pas`);
        }
        if (c.depart < c.min || c.depart > c.max) {
            dire(`curseur ${c.nom} : le départ est hors des bornes`);
        }
        for (const [mot, v] of [
            ['min', c.min],
            ['max', c.max],
            ['départ', c.depart],
        ]) {
            if (!multipleDe(v, c.pas))
                dire(`curseur ${c.nom} : ${mot} n’est pas un multiple du pas`);
        }
    }
    /* La droite baladeuse : un seul curseur, Z, sur une droite. */
    if (reglage.baladeuse !== undefined) {
        if (reglage.famille !== 'lineaire')
            dire('une droite baladeuse est de famille `lineaire`');
        if (reglage.curseurs.length !== 1 || reglage.curseurs[0]?.nom !== CURSEUR_BALADEUSE) {
            dire(`une droite baladeuse a un seul curseur, « ${CURSEUR_BALADEUSE} »`);
        }
        if (reglage.fixes !== undefined)
            dire('une droite baladeuse n’a pas de paramètre fixe');
        if (Math.abs(reglage.baladeuse.y) < EPS) {
            dire('une droite baladeuse x·X + y·Y = Z demande y non nul');
        }
    }
    else {
        if (noms.has(CURSEUR_BALADEUSE)) {
            dire(`le curseur « ${CURSEUR_BALADEUSE} » n’existe que pour une droite baladeuse`);
        }
        for (const k of Object.keys(reglage.fixes ?? {})) {
            if (noms.has(k))
                dire(`« ${k} » est à la fois fixe et réglable`);
        }
    }
    /* La courbe se trace au départ (les lettres sont celles de la famille). */
    const depart = parametresDuReglage(reglage, valeursDeDepart(reglage));
    const lue = fonctionCanoniqueV2(reglage.famille, depart, reglage.domaine);
    if ('griefs' in lue)
        for (const g of lue.griefs)
            dire(`la courbe de départ : ${g}`);
    /* La contrainte qu'elle doit tenir. */
    const contrainte = corrige.contraintes.find((k) => k.type === 'courbe_egale' && k.objet.cible === reglage.objet);
    if (contrainte === undefined) {
        dire('aucune contrainte `courbe_egale` ne vise la cible réglée');
        return griefs;
    }
    if (contrainte.famille !== reglage.famille) {
        dire('la contrainte `courbe_egale` et le réglage ne sont pas de la même famille');
        return griefs;
    }
    const domaineC = contrainte.domaine;
    const domaineR = reglage.domaine;
    if ((domaineC === undefined) !== (domaineR === undefined) ||
        (domaineC !== undefined &&
            domaineR !== undefined &&
            (Math.abs(domaineC[0] - domaineR[0]) > EPS || Math.abs(domaineC[1] - domaineR[1]) > EPS))) {
        dire('le domaine du réglage est celui de la contrainte `courbe_egale`');
    }
    /* Les valeurs attendues, lettre par lettre. */
    const attendues = {};
    for (const [k, v] of Object.entries(contrainte.parametres)) {
        if (k === 'fonction')
            continue;
        const n = valeurEcrite(v);
        if (n !== null)
            attendues[k] = n;
    }
    if (reglage.baladeuse !== undefined) {
        const { x, y } = reglage.baladeuse;
        const a = attendues.a;
        const b = attendues.b;
        if (a === undefined || b === undefined) {
            dire('la contrainte d’une droite baladeuse s’écrit avec a et b');
        }
        else {
            if (Math.abs(a + x / y) > EPS) {
                dire('la pente de la contrainte n’est pas celle de la droite baladeuse (−x/y)');
            }
            const c = reglage.curseurs[0];
            if (c !== undefined)
                atteignable(c, b * y, dire);
        }
    }
    else {
        const lettres = new Set([...noms, ...Object.keys(reglage.fixes ?? {})]);
        const ecrites = new Set(Object.keys(attendues));
        if (lettres.size !== ecrites.size || [...lettres].some((l) => !ecrites.has(l))) {
            dire('la contrainte `courbe_egale` s’écrit avec les mêmes lettres que le réglage (curseurs et fixes)');
        }
        else {
            for (const [k, v] of Object.entries(reglage.fixes ?? {})) {
                if (Math.abs((attendues[k] ?? NaN) - v) > EPS) {
                    dire(`le paramètre fixe ${k} n’est pas celui de la contrainte`);
                }
            }
            for (const c of reglage.curseurs) {
                const v = attendues[c.nom];
                if (v !== undefined)
                    atteignable(c, v, dire);
            }
        }
    }
    /* Le départ n'est pas la réponse : sinon, il suffirait de soumettre. */
    if (griefs.length === 0) {
        const attendue = fonctionCanoniqueV2(contrainte.famille, contrainte.parametres);
        if ('fonction' in attendue &&
            'fonction' in lue &&
            memesFonctions(attendue.fonction, lue.fonction)) {
            dire('au départ, les curseurs donnent déjà la réponse : change le départ');
        }
    }
    return griefs;
}
function atteignable(c, v, dire) {
    if (v < c.min - EPS || v > c.max + EPS) {
        dire(`curseur ${c.nom} : la valeur attendue est hors des bornes`);
    }
    else if (!multipleDe(v - c.min, c.pas)) {
        dire(`curseur ${c.nom} : la valeur attendue n’est pas sur un cran du curseur`);
    }
}
/** Deux fonctions canoniques identiques, à la précision près. */
function memesFonctions(f, g) {
    if (f.famille !== g.famille)
        return false;
    const a = f;
    const b = g;
    return Object.keys(a).every((k) => typeof a[k] === 'number' ? Math.abs(a[k] - b[k]) < EPS : a[k] === b[k]);
}
const nb = (v) => nombreSigne(Number(v.toFixed(4)));
const point = (x, y) => `(${nb(x)}\u00a0;\u00a0${nb(y)})`;
const differe = (u, v) => Math.abs(u - v) > 1e-6;
/**
 * L'ÉCART DIT SUR UN PARAMÈTRE (CONCEPTION § 3, « Tracé d'une courbe ») —
 * après la correction seulement. Le PREMIER paramètre qui diffère, dans
 * l'ordre où il se lit sur la courbe : l'ouverture (a), puis le sommet ou le
 * point de départ, puis le reste. `null` : rien à dire (familles différentes,
 * courbes identiques, ou une écriture illisible) — l'écran garde alors sa
 * phrase générale.
 */
export function ecartDUnParametre(attendue, eleve) {
    if (attendue.famille !== eleve.famille)
        return null;
    const fa = fonctionCanoniqueV2(attendue.famille, attendue.parametres);
    const fe = fonctionCanoniqueV2(eleve.famille, eleve.parametres);
    if (!('fonction' in fa) || !('fonction' in fe))
        return null;
    const a = fa.fonction;
    const e = fe.fonction;
    const parametre = (nom, vEleve, vRegle) => `Ton paramètre ${nom} vaut ${vEleve}\u00a0; celui de la règle vaut ${vRegle}.`;
    switch (a.famille) {
        case 'lineaire': {
            const el = e;
            if (differe(el.a, a.a)) {
                return `Ta droite a un taux de variation de ${nb(el.a)}\u00a0; celle de la règle, de ${nb(a.a)}.`;
            }
            if (differe(el.b, a.b)) {
                return `Ta droite coupe l’axe des y en ${point(0, el.b)}\u00a0; celle de la règle, en ${point(0, a.b)}.`;
            }
            return null;
        }
        case 'quadratique': {
            const el = e;
            if (differe(el.a, a.a))
                return parametre('a', nb(el.a), nb(a.a));
            const sommet = (f) => {
                const h = -f.b / (2 * f.a);
                return [h, evaluerFonctionV2(f, h)];
            };
            const [he, ke] = sommet(el);
            const [ha, ka] = sommet(a);
            if (differe(he, ha) || differe(ke, ka)) {
                return `Ton sommet est en ${point(he, ke)}\u00a0; celui de la règle est en ${point(ha, ka)}.`;
            }
            return null;
        }
        case 'exponentielle': {
            const el = e;
            for (const k of ['a', 'b', 'c']) {
                if (differe(el[k], a[k]))
                    return parametre(k, nb(el[k]), nb(a[k]));
            }
            return null;
        }
        case 'valeur_absolue':
        case 'racine': {
            const el = e;
            for (const k of ['a', 'b']) {
                if (differe(el[k], a[k]))
                    return parametre(k, nb(el[k]), nb(a[k]));
            }
            if (differe(el.h, a.h) || differe(el.k, a.k)) {
                const quoi = a.famille === 'racine' ? 'Ton point de départ' : 'Ton sommet';
                return `${quoi} est en ${point(el.h, el.k)}\u00a0; celui de la règle est en ${point(a.h, a.k)}.`;
            }
            return null;
        }
        case 'sinusoidale':
        case 'tangente': {
            const el = e;
            if (a.famille === 'sinusoidale' &&
                el.famille === 'sinusoidale' &&
                el.fonction !== a.fonction) {
                return null;
            }
            const pi = (v) => texteEnPiV2(v).replace('-', '−').replace('.', ',');
            for (const k of ['a', 'b', 'h', 'k']) {
                const enPi = k === 'b' || k === 'h';
                if (differe(el[k], a[k])) {
                    return parametre(k, enPi ? pi(el[k]) : nb(el[k]), enPi ? pi(a[k]) : nb(a[k]));
                }
            }
            return null;
        }
        case 'logarithmique':
        case 'rationnelle': {
            const ae = a;
            const ee = e;
            for (const k of ['a', 'b', 'c', 'h', 'k']) {
                const va = ae[k];
                const ve = ee[k];
                if (typeof va === 'number' && typeof ve === 'number' && differe(ve, va)) {
                    return parametre(k, nb(ve), nb(va));
                }
            }
            return null;
        }
    }
}
/** Efface le bruit flottant d'un quotient (−2/3). */
function propre(v) {
    return Number(v.toFixed(10));
}
//# sourceMappingURL=reglage.js.map